import gameInfo from '../assets/json/gameInfo.json';
import Phaser from 'phaser';
import gameData, { DROP_MODE, DROP_MODE_GAME_NAME, DROP_MODE_SHORTNAME, MATH_MODE, MATH_MODE_GAME_NAME, MATH_MODE_SHORTNAME } from './gameData.js';
import FontKeys from './FontKeys';
import ABCyaLib from '@abcya/abcya_lib';
import ABCyaPhaser from '@abcya/abcya_phaser';
import { setupAnimations } from './AnimationManager';
import { setup } from './SoundManager';
import box2d, { setupBox2d } from './managers/Box2dObj';

const fonts = [];
for (const k in FontKeys) {
  fonts.push(FontKeys[k]);
}

// note: uncomment or change the following based on what you need to load
ABCyaPhaser.Preloader.PreloaderConfig.fonts = fonts;

/**
 * To load multiple image sprites, change this property to an array of Phaser.Types.Loader.FileTypes.MultiAtlasFileConfig objects.
 * Ex: ABCyaPhaser.Preloader.PreloaderConfig.imagesPath = [
 * { key: ABCyaPhaser.Images.DEFAULT_ATLAS, atlasURL: 'assets/images/' + ABCyaPhaser.Images.DEFAULT_ATLAS + '.json', path: 'assets/images' }, // default multiatlas
 * { key: "particles", atlasURL: 'assets/images/particles.json', path: 'assets/images' }
 * ]
 */
ABCyaPhaser.Preloader.PreloaderConfig.imagesPath = 'assets/images';

/**
 * To load multiple audio sprites, change this property to an array of Phaser.Types.Loader.FileTypes.AudioSpriteFileConfig objects.
 * Note: you should always include an ogg and mp3 file, the game will determine which to use based on compatibility. 
 * Ex: ABCyaPhaser.Preloader.PreloaderConfig.soundsPath = [
 * { key: ABCyaPhaser.Audio.DEFAULT_AUDIO_SPRITE, 
 *   jsonURL: 'assets/sounds/' + DEFAULT_AUDIO_SPRITE + '.json', 
 *   audioURL: [ 'assets/sounds/' + DEFAULT_AUDIO_SPRITE + '.ogg', 'assets/sounds/' + DEFAULT_AUDIO_SPRITE + '.mp3' ] }, // default sound sprite
 * { key: "extrasounds", jsonURL: 'assets/sounds/extrasounds.json', audioURL: [ 'assets/sounds/extrasounds.ogg', 'assets/sounds/extrasounds.mp3' ] }
 * ]
 */
ABCyaPhaser.Preloader.PreloaderConfig.soundsPath = 'assets/sounds';

/**
 * You can load spine objects here. This can be an object with key, jsonPath, and atlasPath properties, or an array of those objects.
 * Ex: ABCyaPhaser.Preloader.PreloaderConfig.spines = [
 * { key: "spine0", jsonPath: "assets/spines/spine0.json", atlasPath: "assets/spines/spine0.atlas" },
 * { key: "spine1", jsonPath: "assets/spines/spine1.json", atlasPath: "assets/spines/spine1.atlas" }
 * ]
 */
// ABCyaPhaser.Preloader.PreloaderConfig.spines = { key: "", jsonPath: "", atlasPath: "" };

ABCyaPhaser.Preloader.PreloaderConfig.startScene = gameData.startScene;

/**
 * Called when the preload function is done.
 * @param {Phaser.Scene} scene the preloader scene
 */
ABCyaPhaser.Preloader.PreloaderConfig.preloadCallback = (scene) => {
  if (gameData.GAME_MODE === MATH_MODE) {
    let localeFolder = 'en-US';
    if (ABCyaLib.Environment.instance.locale === 'en-GB' || ABCyaLib.Environment.instance.locale === 'en-AU') {
      // load the UK/AU version of the files
      localeFolder = 'en-GB';
    }
    scene.load.image('introbackground', `assets/${localeFolder}/addition/IntroBackground.png`);
    scene.load.image('roundselectbackground', `assets/${localeFolder}/addition/RoundSelectBackground.png`);
    scene.load.image('gamebackground', `assets/${localeFolder}/addition/GameBackground.png`);
    scene.load.image('resultsbackground', `assets/${localeFolder}/addition/ResultsBackground.png`);
    scene.load.image('dropbackground', `assets/${localeFolder}/addition/DropBackground.png`);
    scene.load.image('dropresultsbackground', `assets/${localeFolder}/addition/DropResultsBackground.png`);
  }
  else {
    scene.load.image('introbackground', 'assets/images/marble_drop_addition/DropIntroBackground.png');
    scene.load.image('dropbackground', 'assets/images/marble_drop_addition/DropDropBackground.png');
    scene.load.image('dropresultsbackground', 'assets/images/marble_drop_addition/DropDropResultsBackground.png');
  }

  // insert additional preloader functionality here; scene is the preloader scene
  scene.events.once(Phaser.Scenes.Events.CREATE, () => {
    setupBox2d();
    box2d.setup();

    setupAnimations(scene);
    setup(scene);
  });
};

(async function handshake() {
  try {
    await ABCyaLib.Environment.instance.doParentHandshake();
  }
  catch (error) {
    console.warn(error);
  }

  const gameMode = ABCyaLib.getConfig().gameMode;
  if (gameMode !== null && typeof gameMode === 'string' && gameMode.length > 0) {
    // eslint-disable-next-line no-undef
    switch (gameMode) {
      case MATH_MODE:
      {
        gameData.GAME_MODE = MATH_MODE;
        break;
      }
      case DROP_MODE:
      {
        gameData.GAME_MODE = DROP_MODE;
        break;
      }
    }
  }

  // set the scene data based on the game
  if (gameData.GAME_MODE === MATH_MODE) {
    gameData.scenes = gameData.mathScenes;
    gameData.startScene = gameData.mathStartScene;
  }
  else {
    gameData.scenes = gameData.dropScenes;
    gameData.startScene = gameData.dropStartScene;
  }

  // update the preloader config so it loads the correct start scene
  ABCyaPhaser.Preloader.PreloaderConfig.startScene = gameData.startScene;

  console.info('ABCya Game Info:', gameInfo);

  // note: there is a bug with this script when using it with Webpack. Jake F. and Stephen R. could not figure out the problem.
  // loading the script at this point prevents the issue from appearing
  const s = document.createElement('script');
  s.setAttribute('src', './libs/Box2dWeb-2.1.a.3.min.js');
  document.head.appendChild(s);

  /**
     * Use the fourth argument of initializePhaserGame to override default Phaser config options
     * For example: to use the Spine plugin, the fourth argument should look like this:
     * { 
            configOverrides: {
                plugins: {
                    scene: [
                        { key: 'SpinePlugin', plugin: window.SpinePlugin, mapping: 'spine' }
                    ]
                }
            }
        }
     */
  ABCyaPhaser.Misc.initializePhaserGame(1024, 768, gameData.scenes);

  // initialize any functionality in abcya_phaser that has a dependency on abcya_lib 
  ABCyaPhaser.Misc.initializeAbcyaLibDependencies(ABCyaLib);
}());
